/**
 * Worlds Layer Strategy Implementation
 *
 * Implements layer-specific logic for Worlds (W) layer processing.
 * Worlds layer handles both image and video files for environmental and scene assets.
 */

import {
  WORLDS_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../../../constants/apiMetadataConstraints';
import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface WorldsLayerMetadata {
  layerType: 'worlds';

  // Core world information
  worldName?: string;
  worldType?:
    | 'Indoor'
    | 'Outdoor'
    | 'Fantasy'
    | 'Futuristic'
    | 'Historical'
    | 'Abstract';
  description?: string;

  // Environmental characteristics
  setting?: string;
  atmosphere?: string;
  lighting?: string;
  timeOfDay?:
    | 'Dawn'
    | 'Morning'
    | 'Noon'
    | 'Afternoon'
    | 'Evening'
    | 'Night'
    | 'Unknown';
  weather?: string;

  // Visual characteristics
  dominantColors?: string[];
  visualStyle?: string[];
  architecture?: string;
  landscape?: string;

  // Scale and perspective
  scale?: 'Close-up' | 'Medium' | 'Wide' | 'Panoramic' | 'Aerial';
  perspective?: string;
  depth?: 'Shallow' | 'Medium' | 'Deep';

  // Content details
  objects?: string[];
  characters?: boolean;
  vehicles?: boolean;
  nature?: string[];

  // AI-generated analysis
  complexity?: 'Simple' | 'Moderate' | 'Complex';
  mood?: string[];
  energy?: 'Calm' | 'Neutral' | 'Dynamic' | 'Intense';
  immersion?: number; // 0-1 (how immersive the world feels)

  // Technical details (for videos)
  duration?: number; // seconds
  resolution?: string;
  frameRate?: number;
  cameraMovement?: string[];

  // Performance metrics
  viewCount?: number;
  likeCount?: number;
  usageInComposites?: number;
}

export class WorldsLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'W';
  readonly layerName = 'Worlds';
  readonly layerDescription =
    'Environmental scenes, backgrounds, and world assets';

  getRequiredFields(): string[] {
    // Match WorldsMetadataRequest (OpenAPI): mood, worldName, locationType
    return ['worldName', 'locationType', 'mood'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'setting',
      'lighting',
      'timeOfDay',
      'weather',
      'dominantColors',
      'visualStyle',
      'architecture',
      'landscape',
      'scale',
      'perspective',
      'depth',
      'objects',
      'characters',
      'vehicles',
      'nature',
      'complexity',
      'mood',
      'energy',
      'immersion',
      'duration',
      'resolution',
      'frameRate',
      'cameraMovement',
      'viewCount',
      'likeCount',
      'usageInComposites',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'dominantColors',
        'complexity',
        'mood',
        'energy',
        'immersion',
        'duration',
        'resolution',
        'frameRate',
      ],
      inherited: ['atmosphere', 'visualStyle'],
    };
  }

  extractMetadataFromAI(aiResponse: any): WorldsLayerMetadata {
    const layerMetadata =
      aiResponse.data?.layerMetadata ||
      aiResponse.layerMetadata ||
      aiResponse.worldsMetadata ||
      {};

    return {
      layerType: 'worlds',

      // Core information
      worldName: layerMetadata.worldName || aiResponse.worldName || '',
      worldType: this.normalizeWorldType(
        layerMetadata.locationType || layerMetadata.worldType || aiResponse.locationType || aiResponse.worldType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Environmental characteristics
      setting: layerMetadata.setting || aiResponse.setting,
      atmosphere: layerMetadata.atmosphere || aiResponse.atmosphere,
      lighting: layerMetadata.lighting || aiResponse.lighting,
      timeOfDay: this.normalizeTimeOfDay(
        layerMetadata.timeOfDay || aiResponse.timeOfDay
      ),
      weather: layerMetadata.weather || aiResponse.weather,

      // Visual characteristics from AI analysis
      dominantColors: this.normalizeArray(
        layerMetadata.dominantColors || aiResponse.dominantColors
      ),
      visualStyle: this.normalizeArray(
        layerMetadata.visualStyle || aiResponse.visualStyle
      ),
      architecture: layerMetadata.architecture || aiResponse.architecture,
      landscape: layerMetadata.landscape || aiResponse.landscape,

      // Scale and perspective
      scale: this.normalizeScale(layerMetadata.scale || aiResponse.scale),
      perspective: layerMetadata.perspective || aiResponse.perspective,
      depth: this.normalizeDepth(layerMetadata.depth || aiResponse.depth),

      // Content details
      objects: this.normalizeArray(layerMetadata.objects || aiResponse.objects),
      characters: layerMetadata.characters || aiResponse.characters,
      vehicles: layerMetadata.vehicles || aiResponse.vehicles,
      nature: this.normalizeArray(layerMetadata.nature || aiResponse.nature),

      // AI-generated analysis
      complexity: this.normalizeComplexity(
        layerMetadata.complexity || aiResponse.complexity
      ),
      mood: this.normalizeArray(layerMetadata.mood || aiResponse.mood),
      energy: this.normalizeEnergy(layerMetadata.energy || aiResponse.energy),
      immersion: layerMetadata.immersion || aiResponse.immersion,

      // Technical details for videos
      duration: layerMetadata.duration || aiResponse.duration,
      resolution: layerMetadata.resolution || aiResponse.resolution,
      frameRate:
        layerMetadata.frameRate || aiResponse.frameRate || aiResponse.fps,
      cameraMovement: this.normalizeArray(
        layerMetadata.cameraMovement || aiResponse.cameraMovement
      ),
    };
  }

  enhanceWithDefaults(metadata: WorldsLayerMetadata): WorldsLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'worlds',

      // Ensure required fields have defaults
      worldName: metadata.worldName || 'Untitled World',
      worldType: metadata.worldType || 'Outdoor',

      // Normalize arrays
      atmosphere: metadata.atmosphere,
      dominantColors: this.normalizeArray(metadata.dominantColors),
      visualStyle: this.normalizeArray(metadata.visualStyle),
      objects: this.normalizeArray(metadata.objects),
      nature: this.normalizeArray(metadata.nature),
      mood: this.normalizeArray(metadata.mood),
      cameraMovement: this.normalizeArray(metadata.cameraMovement),

      // Default AI values
      complexity: metadata.complexity || 'Moderate',
      energy: metadata.energy || 'Neutral',
      timeOfDay: metadata.timeOfDay || 'Unknown',
    };
  }

  generateTags(metadata: WorldsLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('worlds');
    tags.add('environment');
    tags.add('scene');

    // World type tags
    if (metadata.worldType) {
      tags.add(metadata.worldType.toLowerCase());
      switch (metadata.worldType) {
        case 'Indoor':
          tags.add('interior');
          tags.add('inside');
          break;
        case 'Outdoor':
          tags.add('exterior');
          tags.add('outside');
          tags.add('landscape');
          break;
        case 'Fantasy':
          tags.add('magical');
          tags.add('fantastical');
          break;
        case 'Futuristic':
          tags.add('sci-fi');
          tags.add('future');
          tags.add('cyberpunk');
          break;
        case 'Historical':
          tags.add('period');
          tags.add('vintage');
          tags.add('retro');
          break;
        case 'Abstract':
          tags.add('surreal');
          tags.add('conceptual');
          break;
      }
    }

    // Setting tags
    if (metadata.setting) {
      tags.add(metadata.setting.toLowerCase().replace(/\s+/g, '-'));
    }

    // Atmosphere tags
    if (metadata.atmosphere) {
        tags.add(metadata.atmosphere.toLowerCase().replace(/\s+/g, '-'));
    }

    // Time of day tags
    if (metadata.timeOfDay && metadata.timeOfDay !== 'Unknown') {
      tags.add(metadata.timeOfDay.toLowerCase());
      if (['Dawn', 'Morning'].includes(metadata.timeOfDay)) {
        tags.add('early');
      } else if (['Evening', 'Night'].includes(metadata.timeOfDay)) {
        tags.add('late');
      }
    }

    // Weather tags
    if (metadata.weather) {
      tags.add(metadata.weather.toLowerCase());
    }

    // Visual style tags
    if (metadata.visualStyle && metadata.visualStyle.length > 0) {
      metadata.visualStyle.forEach(style => {
        tags.add(style.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Scale tags
    if (metadata.scale) {
      tags.add(metadata.scale.toLowerCase().replace(/-/g, ''));
      switch (metadata.scale) {
        case 'Close-up':
          tags.add('detailed');
          tags.add('intimate');
          break;
        case 'Wide':
        case 'Panoramic':
          tags.add('expansive');
          tags.add('broad');
          break;
        case 'Aerial':
          tags.add('overhead');
          tags.add('birds-eye');
          break;
      }
    }

    // Mood tags
    if (metadata.mood && metadata.mood.length > 0) {
      metadata.mood.forEach(m => {
        tags.add(`${m.toLowerCase()}-mood`);
        tags.add(m.toLowerCase());
      });
    }

    // Energy tags
    if (metadata.energy) {
      tags.add(`${metadata.energy.toLowerCase()}-energy`);
    }

    // Complexity tags
    if (metadata.complexity) {
      tags.add(`${metadata.complexity.toLowerCase()}-complexity`);
    }

    // Content-specific tags
    if (metadata.characters) {
      tags.add('with-characters');
      tags.add('populated');
    }

    if (metadata.vehicles) {
      tags.add('with-vehicles');
      tags.add('transportation');
    }

    // Nature tags
    if (metadata.nature && metadata.nature.length > 0) {
      metadata.nature.forEach(n => {
        tags.add(n.toLowerCase());
      });
      tags.add('natural');
    }

    // Architecture tags
    if (metadata.architecture) {
      tags.add(metadata.architecture.toLowerCase().replace(/\s+/g, '-'));
      tags.add('architectural');
    }

    // Camera movement tags (for videos)
    if (metadata.cameraMovement && metadata.cameraMovement.length > 0) {
      metadata.cameraMovement.forEach(movement => {
        tags.add(movement.toLowerCase().replace(/\s+/g, '-'));
      });
      tags.add('cinematic');
    }

    // Duration-based tags (for videos)
    if (metadata.duration) {
      if (metadata.duration < 10) {
        tags.add('short-clip');
      } else if (metadata.duration > 60) {
        tags.add('long-scene');
      }
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and main environment
      if (
        lowerTag.includes('world') ||
        lowerTag.includes('environment') ||
        lowerTag.includes('scene') ||
        ['indoor', 'outdoor', 'fantasy', 'futuristic', 'historical'].includes(
          lowerTag
        )
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - atmosphere and mood
      else if (
        lowerTag.includes('mood') ||
        lowerTag.includes('energy') ||
        lowerTag.includes('atmosphere') ||
        lowerTag.includes('time')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: WorldsLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // OpenAPI WorldsMetadataRequest: string maxLength 255, dominantColors max 10
    if (metadata.worldName) {
      const msg = validateStringMaxLength(metadata.worldName, WORLDS_METADATA.stringMaxLength, 'World name');
      if (msg) errors.push({ field: 'worldName', message: msg, severity: 'error' });
    }
    const locationType = (metadata as any).locationType;
    if (locationType) {
      const msg = validateStringMaxLength(locationType, WORLDS_METADATA.stringMaxLength, 'Location type');
      if (msg) errors.push({ field: 'locationType', message: msg, severity: 'error' });
    }
    const dominantColors = (metadata as any).dominantColors ?? metadata.dominantColors;
    if (dominantColors && Array.isArray(dominantColors)) {
      const msg = validateArrayLength(dominantColors, WORLDS_METADATA.dominantColors, 'Dominant colors');
      if (msg) errors.push({ field: 'dominantColors', message: msg, severity: 'error' });
      else {
        const itemMsg = validateArrayItems(dominantColors, WORLDS_METADATA.stringMaxLength, 'Dominant colors');
        if (itemMsg) errors.push({ field: 'dominantColors', message: itemMsg, severity: 'error' });
      }
    }

    // Business rule: World name should be meaningful
    if (metadata.worldName && metadata.worldName.trim().length < 3) {
      warnings.push({
        field: 'worldName',
        message: 'World name should be at least 3 characters long',
        suggestion: 'Provide a more descriptive world name',
      });
    }

    // Business rule: Should have environmental characteristics
    if (
      !metadata.setting &&
      !metadata.atmosphere?.length &&
      !metadata.lighting
    ) {
      warnings.push({
        field: 'setting',
        message:
          'World should have at least one environmental characteristic (setting, atmosphere, or lighting)',
        suggestion: 'Add setting, atmosphere, or lighting information',
      });
    }

    // Business rule: Duration should be reasonable for videos
    if (metadata.duration) {
      if (metadata.duration < 1) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very short (< 1 second)',
          suggestion: 'Verify the video duration',
        });
      } else if (metadata.duration > 600) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very long (> 10 minutes)',
          suggestion: 'Consider using shorter clips for world assets',
        });
      }
    }

    // Business rule: Frame rate should be standard for videos
    if (
      metadata.frameRate &&
      ![24, 25, 30, 48, 50, 60].includes(metadata.frameRate)
    ) {
      warnings.push({
        field: 'frameRate',
        message: `Non-standard frame rate: ${metadata.frameRate} fps`,
        suggestion: 'Common frame rates are 24, 30, or 60 fps',
      });
    }

    // Business rule: Immersion should be 0-1
    if (
      metadata.immersion &&
      (metadata.immersion < 0 || metadata.immersion > 1)
    ) {
      warnings.push({
        field: 'immersion',
        message: 'Immersion should be a value between 0 and 1',
        suggestion: 'Provide a decimal value between 0 and 1',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): WorldsLayerMetadata {
    return {
      layerType: 'worlds',
      worldName: '',
      worldType: 'Outdoor',
      description: '',
      atmosphere: '',
      dominantColors: [],
      visualStyle: [],
      objects: [],
      nature: [],
      mood: [],
      cameraMovement: [],
      complexity: 'Moderate',
      energy: 'Neutral',
      timeOfDay: 'Unknown',
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Worlds are unique - don't inherit names
      worldName: 'never',
      worldType: 'never',
      setting: 'never',

      // Could inherit atmospheric characteristics from collections
      atmosphere: 'merge',
      visualStyle: 'merge',
      mood: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeWorldType(value: any): WorldsLayerMetadata['worldType'] {
    if (!value) return 'Outdoor';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('indoor') ||
      normalized.includes('interior') ||
      normalized.includes('inside')
    )
      return 'Indoor';
    if (
      normalized.includes('fantasy') ||
      normalized.includes('magical') ||
      normalized.includes('myth')
    )
      return 'Fantasy';
    if (
      normalized.includes('future') ||
      normalized.includes('sci') ||
      normalized.includes('cyber')
    )
      return 'Futuristic';
    if (
      normalized.includes('historical') ||
      normalized.includes('period') ||
      normalized.includes('vintage')
    )
      return 'Historical';
    if (
      normalized.includes('abstract') ||
      normalized.includes('surreal') ||
      normalized.includes('concept')
    )
      return 'Abstract';

    return 'Outdoor';
  }

  private normalizeTimeOfDay(value: any): WorldsLayerMetadata['timeOfDay'] {
    if (!value) return 'Unknown';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('dawn') || normalized.includes('sunrise'))
      return 'Dawn';
    if (normalized.includes('morning')) return 'Morning';
    if (normalized.includes('noon') || normalized.includes('midday'))
      return 'Noon';
    if (normalized.includes('afternoon')) return 'Afternoon';
    if (normalized.includes('evening') || normalized.includes('sunset'))
      return 'Evening';
    if (normalized.includes('night') || normalized.includes('midnight'))
      return 'Night';

    return 'Unknown';
  }

  private normalizeScale(value: any): WorldsLayerMetadata['scale'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('close') || normalized.includes('detail'))
      return 'Close-up';
    if (normalized.includes('wide') || normalized.includes('broad'))
      return 'Wide';
    if (normalized.includes('panoramic') || normalized.includes('pan'))
      return 'Panoramic';
    if (
      normalized.includes('aerial') ||
      normalized.includes('overhead') ||
      normalized.includes('bird')
    )
      return 'Aerial';

    return 'Medium';
  }

  private normalizeDepth(value: any): WorldsLayerMetadata['depth'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('shallow') || normalized.includes('flat'))
      return 'Shallow';
    if (normalized.includes('deep') || normalized.includes('dimensional'))
      return 'Deep';

    return 'Medium';
  }

  private normalizeComplexity(value: any): WorldsLayerMetadata['complexity'] {
    if (!value) return 'Moderate';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('simple') ||
      normalized.includes('basic') ||
      normalized.includes('minimal')
    )
      return 'Simple';
    if (
      normalized.includes('complex') ||
      normalized.includes('elaborate') ||
      normalized.includes('detailed')
    )
      return 'Complex';

    return 'Moderate';
  }

  private normalizeEnergy(value: any): WorldsLayerMetadata['energy'] {
    if (!value) return 'Neutral';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('calm') ||
      normalized.includes('peaceful') ||
      normalized.includes('serene')
    )
      return 'Calm';
    if (
      normalized.includes('dynamic') ||
      normalized.includes('active') ||
      normalized.includes('lively')
    )
      return 'Dynamic';
    if (
      normalized.includes('intense') ||
      normalized.includes('dramatic') ||
      normalized.includes('exciting')
    )
      return 'Intense';

    return 'Neutral';
  }
}

// Export singleton instance
export const worldsLayerStrategy = new WorldsLayerStrategy();
